<template>
  <ScrollTop />
  <footer class="main-footer-container" id="page-footer">
    <GridContainer>
      <GridItem :size-xl="10" :offset-xl="1">
        <NewsletterSignup />
      </GridItem>
    </GridContainer>
    <GridContainer>
      <GridItem class="top-divider" :size-xl="10" :offset-xl="1" />
      <GridItem
        :size-md="2"
        :size-lg="3"
        :size-xl="2"
        :offset-xl="1"
        class="footer-logo-container"
      >
        <StoryblokImg
          :image="{
            filename:
              'https://a.storyblok.com/f/223704/125x26/bb79b70cf7/cpj_logo.svg',
            alt: 'ComparaJá.pt',
          }"
        />
      </GridItem>
      <GridItem
        class="social-links-container"
        :size-md="2"
        :size-lg="9"
        :size-xl="8"
        v-if="socialLinks"
      >
        <div class="social-links-wrapper">
          <h4>{{ t("socials.title") }}</h4>
          <ul class="social-links-list">
            <li v-for="social in socialLinks">
              <NuxtLink
                :class="social.socialNetwork"
                :href="getStoryblokUrl(social.link)"
                :target="social.link.target"
                :aria-label="social.socialNetwork"
              />
            </li>
          </ul>
        </div>
      </GridItem>
      <GridItem class="bottom-divider" :size-xl="10" :offset-xl="1" />
    </GridContainer>
    <GridContainer>
      <GridItem
        class="disclaimer-container"
        :size-lg="3"
        :size-xl="2"
        :offset-xl="1"
      >
        <div class="disclaimer">
          <StoryblokImg
            :image="{
              filename:
                'https://a.storyblok.com/f/223704/204x96/8097e2599b/logo_banco_portugal.svg',
              alt: 'Banco de Portugal',
            }"
          />
          <div class="text-container">
            <div class="authorization">
              {{ t("footer.bank_disclaimer.authorized") }}
            </div>
            <NuxtLink :href="'/intermediario-de-credito'" class="intermediary">
              {{ t("footer.bank_disclaimer.intermediary") }}
            </NuxtLink>
          </div>
        </div>
        <div class="disclaimer">
          <StoryblokImg
            :image="{
              filename:
                'https://a.storyblok.com/f/223704/485x260/9a32ea0984/logo_asf.png',
              alt: 'Logo ASF',
            }"
          />
          <div class="text-container">
            {{ t("footer.asf_disclaimer.authorized") }}
          </div>
        </div>
      </GridItem>
      <GridItem class="bottom-divider" v-if="viewport.isLessThan('lg')" />
      <GridItem class="navigation-container" :size-lg="9" :size-xl="8">
        <div class="footer-navigation">
          <Scaffold v-if="viewport.isGreaterOrEquals('md')">
            <nav class="nav-column" v-for="navColumn in navGroups">
              <div class="nav-group" v-for="category in navColumn">
                <div class="nav-group-title">
                  {{ category.title }}
                </div>
                <ul class="link-list" v-if="category.links?.length">
                  <li v-for="footerLink in category.links">
                    <NuxtLink
                      class="footer-link"
                      :href="getStoryblokUrl(footerLink.link)"
                      :target="footerLink.link.target"
                    >
                      {{ footerLink.text }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </nav>
          </Scaffold>
          <Scaffold v-else>
            <Scaffold v-for="navColumn in navGroups">
              <Scaffold v-for="category in navColumn">
                <CollapseContainer v-if="category.links?.length">
                  <template #header>
                    <div class="nav-group-title">{{ category.title }}</div>
                  </template>
                  <template #content>
                    <nav>
                      <ul class="link-list">
                        <li v-for="footerLink in category.links">
                          <NuxtLink
                            class="footer-link"
                            :href="getStoryblokUrl(footerLink.link)"
                            :target="footerLink.link.target"
                          >
                            {{ footerLink.text }}
                          </NuxtLink>
                        </li>
                      </ul>
                    </nav>
                  </template>
                </CollapseContainer>
              </Scaffold>
            </Scaffold>
          </Scaffold>
        </div>
        <FooterContact />
      </GridItem>
    </GridContainer>
  </footer>
</template>

<script lang="ts" setup>
import {
  CollapseContainer,
  Scaffold,
  GridContainer,
  GridItem,
} from "cja-phoenix";
import { getStoryblokUrl } from "@/utils/getStoryblokUrl";
import { FooterStoryblok } from "~/types/bloks";

const props = defineProps<{
  footer: FooterStoryblok;
}>();

const { t } = useI18n();

const viewport = useViewport();

const { navGroups, socialLinks } = (() => ({
  navGroups: Object.entries(props.footer)
    .filter((s) => s[0].includes("footerColumn"))
    .map((s) => s[1]),
  socialLinks: props.footer.socialLinks,
}))();
</script>

<style lang="scss" scoped>
.main-footer-container {
  padding: 40px 0;
  background: linear-gradient(80deg, $extra-light-blue 1%, $white 100%), $white;

  .top-divider,
  .bottom-divider {
    margin: 32px 0;
    height: 1px;
    background-color: $light-grey;
  }

  .bottom-divider {
    @media screen and (min-width: $break-lg-min) {
      margin-top: 0;
      height: 0;
    }
  }

  .footer-logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;

    @media screen and (min-width: $break-md-min) {
      margin-bottom: 0;
      justify-content: flex-start;
    }

    img {
      display: block;
      width: 150px;
    }
  }

  .social-links-container {
    display: flex;
    justify-content: center;

    @media screen and (min-width: $break-md-min) {
      justify-content: flex-start;
    }

    .social-links-wrapper {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;

      @media screen and (min-width: $break-md-min) {
        justify-content: flex-end;
        align-items: flex-start;
      }

      @media screen and (min-width: $break-lg-min) {
        flex-direction: row;
        align-items: center;
        gap: 32px;
      }
    }

    h4 {
      font-size: 20px;
      font-weight: 700;
      margin: 0;
      flex-shrink: 0;

      @media screen and (min-width: $break-lg-min) {
        font-size: 18px;
      }
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: row;
      gap: 8px;

      @media screen and (min-width: $break-lg-min) {
        justify-content: center;
      }

      a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        font-size: 20px;
        transition: all 0.3s ease-in-out;
        color: $main-blue;

        &:hover {
          text-decoration: none;
          color: $dark-blue;
        }
      }
    }
  }

  .disclaimer-container {
    display: grid;
    gap: 40px;

    @media screen and (min-width: $break-md-min) and (max-width: $break-md-max) {
      grid-template-columns: repeat(2, 1fr);
    }

    .disclaimer {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 12px;
      font-size: 13px;
      line-height: 16px;

      @media screen and (max-width: $break-sm-max) {
        max-width: 400px;
        margin: 0 auto;
      }

      @media screen and (min-width: $break-md-min) {
        text-align: left;
      }

      @media screen and (min-width: $break-md-min) and (max-width: $break-md-max) {
        align-items: flex-start;
        text-align: left;
        margin: 0;
        max-width: none;
      }

      @media screen and (min-width: $break-lg-min) {
        align-items: unset;
        margin-bottom: 0;
      }

      img {
        width: 115px;
        display: block;
      }

      .authorization {
        font-weight: 700;
        margin-bottom: 8px;

        @media screen and (min-width: $break-lg-min) {
          margin-bottom: 12px;
        }
      }

      .intermediary {
        color: inherit;
        text-decoration: underline;
      }
    }

    + .bottom-divider {
      margin: 40px 0;
    }
  }

  .navigation-container {
    display: grid;
    gap: 32px;

    .footer-navigation {
      display: grid;
      gap: 40px;

      @media screen and (min-width: $break-md-min) {
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
      }

      .collapse-container {
        width: 100%;
        max-width: 400px;
        margin: 0 auto;
      }

      .nav-column {
        display: flex;
        flex-direction: column;
        gap: 32px;

        .nav-group {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
      }

      .nav-group-title {
        font-size: 16px;
        line-height: 18px;
        font-weight: 700;
      }

      .link-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        font-size: 14px;
        line-height: 16px;
        gap: 24px;

        @media screen and (min-width: $break-md-min) {
          gap: 8px;
        }

        a {
          color: inherit;
        }
      }
    }

    .footer-contact {
      @media screen and (min-width: $break-md-min) {
        justify-self: flex-start;
        max-width: none;
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "pt": {
    "socials": {
      "title": "Segue-nos nas redes sociais"
    }
  }
}
</i18n>
